import { useEffect, useState } from 'react'
import { Role, User } from '@/types/user.type'
import axios, { endpoints } from '@/lib/axios'
import { PaginateResponse } from '@/utils/paginate'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import { Card, CardContent } from '@/components/ui/card'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useAuthContext } from '@/contexts/auth-context/auth-provider'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select'

import { OrderStatus, OrderFilters, ShippingStatus } from './types'

interface OrdersFilterProps {
  filters: OrderFilters
  onFiltersChange: (filters: Partial<OrderFilters>) => void
}

const STATUS_TABS = [
  { label: 'ทั้งหมด', value: OrderStatus.ALL },
  { label: 'รอชำระเงิน', value: OrderStatus.PENDING },
  { label: 'ชำระเงินแล้ว', value: OrderStatus.PAYMENT },
  { label: 'สำเร็จ', value: OrderStatus.COMPLETED },
  { label: 'ยกเลิก', value: OrderStatus.CANCEL },
] as const

const SHIPPING_STATUS_TABS = [
  { label: 'ทั้งหมด', value: ShippingStatus.ALL },
  { label: 'รอจัดส่ง', value: ShippingStatus.PENDING },
  { label: 'จัดส่งแล้ว', value: ShippingStatus.SHIPPED },
  { label: 'จัดส่งสำเร็จ', value: ShippingStatus.DELIVERED },
  { label: 'ยกเลิก', value: ShippingStatus.CANCELLED },
] as const

export default function OrdersFilter({
  filters,
  onFiltersChange,
}: OrdersFilterProps) {
  const { user } = useAuthContext()

  const [shops, setShops] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [hasMore, setHasMore] = useState(true)
  const [page, setPage] = useState(1)
  // const [searchInput, setSearchInput] = useState(filters.search)

  // const handleSearchSubmit = (e: React.FormEvent) => {
  //   e.preventDefault()
  //   onFiltersChange({
  //     search: searchInput,
  //     page: 1, // Reset to first page when searching
  //   })
  // }

  const handleStatusChange = (status: OrderStatus) => {
    onFiltersChange({
      status,
      page: 1, // Reset to first page when filtering
    })
  }

  const handleShippingStatusChange = (status: ShippingStatus) => {
    onFiltersChange({
      shippingStatus: status,
      page: 1, // Reset to first page when filtering
    })
  }

  const handleShopChange = (shopId: string) => {
    onFiltersChange({
      shopId: shopId === 'all' ? undefined : shopId,
      page: 1, // Reset to first page when filtering
    })
  }

  const isAdmin = user?.role === Role.ADMIN

  const fetchShops = async (pageNumber = 1, append = false) => {
    setLoading(pageNumber === 1)
    try {
      const { data } = await axios.get<PaginateResponse<User>>(
        endpoints.users.list,
        {
          params: {
            role: 'SHOP',
            page: pageNumber,
            limit: 5,
          },
        },
      )

      setShops((prev) => (append ? [...prev, ...data.items] : data.items))
      setHasMore(pageNumber * data.limit < data.total)
    } catch (error) {
      console.error('Error fetching shops:', error)
      setHasMore(false)
    } finally {
      setLoading(false)
    }
  }

  const fetchMoreData = () => {
    if (!loading && hasMore) {
      const nextPage = page + 1
      setPage(nextPage)
      fetchShops(nextPage, true)
    }
  }

  useEffect(() => {
    if (isAdmin) {
      fetchShops(1, false)
    }
  }, [])

  return (
    <Card>
      <CardContent>
        <div className="flex flex-col gap-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-black">รายการออเดอร์</h2>
          </div>

          <Separator className="border-gray-200" />

          {/* Search Bar */}
          {/* <form onSubmit={handleSearchSubmit} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="ค้นหาจากเลขที่ออเดอร์, ชื่อลูกค้า หรือเบอร์โทร..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-10 rounded-lg border"
              />
            </div>
            <Button type="submit" variant="outline">
              ค้นหา
            </Button>
          </form> */}

          <div className="flex gap-6 flex-wrap">
            {/* Status Filter Tabs */}
            <div className="space-y-1">
              <p className="text-sm font-medium text-gray-500 mb-1">
                สถานะออเดอร์
              </p>
              <Select
                value={filters.status}
                onValueChange={(value) =>
                  handleStatusChange(value as OrderStatus)
                }
              >
                <SelectTrigger className="min-w-[140px]">
                  <SelectValue placeholder="เลือกสถานะออเดอร์" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_TABS.map((tab) => (
                    <SelectItem key={tab.value} value={tab.value}>
                      {tab.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <p className="text-sm font-medium text-gray-500 mb-1">
                สถานะการจัดส่ง
              </p>
              <Select
                value={filters.shippingStatus}
                onValueChange={(value) =>
                  handleShippingStatusChange(value as ShippingStatus)
                }
              >
                <SelectTrigger className="min-w-[140px]">
                  <SelectValue placeholder="เลือกสถานะการจัดส่ง" />
                </SelectTrigger>
                <SelectContent>
                  {SHIPPING_STATUS_TABS.map((tab) => (
                    <SelectItem key={tab.value} value={tab.value}>
                      {tab.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {isAdmin && (
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-500 mb-1">
                  ร้านค้า
                </p>
                <Select
                  value={filters.shopId || 'all'}
                  onValueChange={handleShopChange}
                  disabled={loading}
                >
                  <SelectTrigger className="min-w-[140px]">
                    <SelectValue placeholder="เลือกร้านค้า" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[200px] overflow-hidden">
                    <SelectItem value="all">ทั้งหมด</SelectItem>
                    <div
                      id="shop-select-content"
                      className="max-h-[150px] overflow-y-auto"
                    >
                      <InfiniteScroll
                        dataLength={shops.length}
                        next={fetchMoreData}
                        hasMore={hasMore}
                        loader={
                          <div className="p-2">
                            <Skeleton className="h-6 w-full" />
                          </div>
                        }
                        endMessage={
                          shops.length > 0 ? (
                            <div className="p-2 text-center text-xs text-gray-500">
                              ไม่มีร้านค้าเพิ่มเติม
                            </div>
                          ) : null
                        }
                        scrollableTarget="shop-select-content"
                      >
                        {shops.map((shop) => (
                          <SelectItem key={shop.uid} value={shop.uid}>
                            {shop.displayName ||
                              `${shop.firstName} ${shop.lastName}`}
                          </SelectItem>
                        ))}
                      </InfiniteScroll>
                    </div>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
